import { Tooltip, IconButton } from '@radix-ui/themes';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import Cookies from 'js-cookie';
import Swal from 'sweetalert2';
import { CheckIcon, NotInterestedIcon } from 'shared/icons';

import BaseApi from 'Components/api/BaseApi';
import ApiKey from 'Components/api/ApiKey';

import { useJobApplicants } from './job-applicants-provider';
import { logOutIfTokenExpired, resultInfo } from 'Components/helpers';
import { useNavigate, useParams } from 'react-router-dom';

function JobToggleStatusView() {
   const { t } = useTranslation('global');
   const { slug: routeSlug } = useParams();
   const jobApplicants = useJobApplicants();
   const tokenKey = Cookies.get('tokenClient');
   const navigate = useNavigate();

   const handleActivate = async (slug?: string) => {
      try {
         const confirmationResult = await Swal.fire({
            icon: 'question',
            showCancelButton: true,
            cancelButtonText: t('employerInnerAccountdetail.no'),
            confirmButtonText: t('employerInnerAccountdetail.yes'),
            text: t('employerInnerAccountdetail.activateJobConfirmTxt'),
            title: t('employerInnerAccountdetail.activateJobConfirmTitle'),
         });

         if (confirmationResult.isConfirmed) {
            const response = await axios.post(BaseApi + `/job/activate/${slug}`, null, {
               headers: {
                  key: ApiKey,
                  token: tokenKey,
                  contentType: 'application/json',
               },
            });
            if (response.data.status === 200) {
               resultInfo(
                  'success',
                  t('employerInnerAccountdetail.activateJobSuccessTitle'),
                  t('employerInnerAccountdetail.close')
               ); // eslint-disable-line
               jobApplicants.loadAccDetail({ slug: routeSlug ?? '' });
            } else if (response.data.status === 400) {
               navigate('/');

               Cookies.remove('fname');
               Cookies.remove('user_id');
               Cookies.remove('user_type');
               Cookies.remove('tokenClient');

               resultInfo('warning', response.data.message, t('employerInnerAccountdetail.close'));
            } else {
               Swal.fire({
                  icon: 'error',
                  text: response.data.message,
                  confirmButtonText: t('employerInnerAccountdetail.close'),
                  title: t('employerFavouriteListProfile.addFavFailedTitle'),
               });
            }
         }
      } catch (error) {
         if ((error instanceof Error && error.message === 'Network Error') || error) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         } else {
            resultInfo(
               'error',
               t('employerInnerAccountdetail.activateJobFailedTitle'),
               t('employerInnerAccountdetail.close')
            ); // eslint-disable-line
         }

         console.error('Error activating job:', error);
      }
   };

   const handleDeactivate = async (slug?: string) => {
      try {
         const confirmationResult = await Swal.fire({
            icon: 'question',
            showCancelButton: true,
            cancelButtonText: t('employerInnerAccountdetail.no'),
            confirmButtonText: t('employerInnerAccountdetail.yes'),
            text: t('employerInnerAccountdetail.deactivateJobConfirmTxt'),
            title: t('employerInnerAccountdetail.deactivateJobConfirmTitle'),
         });

         if (confirmationResult.isConfirmed) {
            const response = await axios.post(BaseApi + `/job/deactivate/${slug}`, null, {
               headers: {
                  key: ApiKey,
                  token: tokenKey,
                  contentType: 'application/json',
               },
            });

            if (response.data.status === 200) {
               resultInfo(
                  'success',
                  t('employerInnerAccountdetail.deactivateJobSuccessTitle'),
                  t('employerInnerAccountdetail.close')
               ); // eslint-disable-line
               jobApplicants.loadAccDetail({ slug: routeSlug ?? '' });
            } else if (response.data.status === 400) {
               navigate('/');

               Cookies.remove('fname');
               Cookies.remove('user_id');
               Cookies.remove('user_type');
               Cookies.remove('tokenClient');

               resultInfo('warning', response.data.message, t('employerInnerAccountdetail.close'));
            } else {
               Swal.fire({
                  icon: 'error',
                  text: response.data.message,
                  confirmButtonText: t('employerInnerAccountdetail.close'),
                  title: t('employerFavouriteListProfile.addFavFailedTitle'),
               });
            }
         }
      } catch (error) {
         if ((error instanceof Error && error.message === 'Network Error') || error) {
            navigate('/');

            logOutIfTokenExpired(t('tokenExpired.tokenExpired'), t('tokenExpired.close'));
         } else {
            resultInfo(
               'error',
               t('employerInnerAccountdetail.deactivateJobFailedTitle'),
               t('employerInnerAccountdetail.close')
            ); // eslint-disable-line
         }

         console.error('Error deactivating job:', error);
      }
   };

   return (
      <div className="JobseekerProfileDetailsBox">
         <div className="JobseekerProfileActive manageJobActionButton">
            <div className="CreatedDate">
               {t('employerInnerAccountdetail.created')}{' '}
               <strong>{jobApplicants.jobInfo?.created}</strong>
            </div>
            {jobApplicants.jobInfo?.status === 1 ? (
               <Tooltip content={t('employerInnerAccountdetail.deactivate')}>
                  <IconButton
                     size="1"
                     ml="2"
                     style={{ background: 'green' }}
                     color="green"
                     onClick={() => handleDeactivate(jobApplicants.jobInfo?.slug)}
                  >
                     <CheckIcon width="20" height="20" />
                  </IconButton>
               </Tooltip>
            ) : (
               <Tooltip content={t('employerInnerAccountdetail.activate')}>
                  <IconButton
                     size="1"
                     ml="2"
                     color='red'
                     highContrast
                     onClick={() => handleActivate(jobApplicants.jobInfo?.slug)}
                  >
                     <NotInterestedIcon width={20} height={20} />
                  </IconButton>
               </Tooltip>
            )}
         </div>
      </div>
   );
}

export const JobToggleStatus = observer(JobToggleStatusView);
